import React, { useState } from 'react';
import { getEntityId } from 'helpers/entityId';
import classNames from 'classnames';
import HumanReadableTime from 'components/utils/HumanReadableTime';
import { Button, Spinner, Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faThumbtack,
  faReply,
  faEllipsisH,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { JobComment } from 'services/api/job';
import {
  CommentType,
  PIN_COMMENT,
  UNPIN_COMMENT,
  REPLY_COMMENT,
  DELETE_COMMENT
} from 'services/api/comment';

import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import '../modules/project-management/card-view/styles/comments.css';
import MentionInput from './MentionInput';
import { MentionUser } from 'types/mention';
import UserAvatar from './CommentUserAvatar';

interface JobCommentsProps {
  comments: JobComment[];
  className?: string;
  jobId?: string;
}

export const JobComments = ({
  comments,
  className,
  jobId
}: JobCommentsProps) => {
  const [filter, setFilter] = useState<CommentType>(CommentType.All);
  const [localComments, setLocalComments] = useState<JobComment[]>(comments);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState<string>('');
  const [mentionedUsers, setMentionedUsers] = useState<MentionUser[]>([]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const user = JSON.parse(localStorage.getItem('authUser') as string);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    const structuredComments = structureComments(comments);
    setLocalComments(structuredComments);
  }, [comments]);

  const convertLinksToClickable = (text: string): string => {
    const urlRegex =
      /(https?:\/\/[^\s]+|www\.[^\s]+|[^\s]+\.[a-z]{2,}(?:\/[^\s]*)?)/gi;

    return text.replace(urlRegex, url => {
      const href = url.startsWith('http') ? url : `https://${url}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-primary text-decoration-underline">${url}</a>`;
    });
  };

  const processCommentContent = (content: string): string => {
    const withMentions = content.replace(
      /@(\w+\s+\w+)/g,
      '<span class="badge bg-primary-subtle text-primary mention-badge">@$1</span>'
    );

    return convertLinksToClickable(withMentions);
  };

  const removeCommentFromLocal = (
    comments: JobComment[],
    commentIdToRemove: string
  ): JobComment[] => {
    return comments
      .filter(comment => (comment.id || comment._id) !== commentIdToRemove)
      .map(comment => ({
        ...comment,
        replies: comment.replies
          ? removeCommentFromLocal(comment.replies, commentIdToRemove)
          : []
      }));
  };

  const structureComments = (flatComments: JobComment[]): JobComment[] => {
    const commentMap = new Map<string, JobComment>();
    const rootComments: JobComment[] = [];

    flatComments.forEach(comment => {
      commentMap.set(comment.id || comment._id, { ...comment, replies: [] });
    });

    flatComments.forEach(comment => {
      const commentWithReplies = commentMap.get(comment.id || comment._id);
      if (!commentWithReplies) return;

      if (comment.parentCommentId) {
        const parent = commentMap.get(comment.parentCommentId);
        if (parent) {
          if (!parent.replies) parent.replies = [];
          parent.replies.push(commentWithReplies);
        }
      } else {
        rootComments.push(commentWithReplies);
      }
    });

    return rootComments;
  };

  // Socket handling removed - managed by parent CardViewModal to prevent duplicate event handling

  const pinCommentMutation = useMutation(PIN_COMMENT, {
    onSuccess: () => {
      queryClient.refetchQueries(['stickyComments', jobId]);
      queryClient.refetchQueries(['jobComments', jobId]);
      queryClient.refetchQueries('getJobs');
      toast.success('Comment pinned successfully');
    },
    onError: () => {
      toast.error('Failed to pin comment');
    }
  });

  const unpinCommentMutation = useMutation(UNPIN_COMMENT, {
    onSuccess: () => {
      queryClient.refetchQueries(['stickyComments', jobId]);
      queryClient.refetchQueries(['jobComments', jobId]);
      queryClient.refetchQueries('getJobs');
      toast.success('Comment unpinned successfully');
    },
    onError: () => {
      toast.error('Failed to unpin comment');
    }
  });

  const replyCommentMutation = useMutation(REPLY_COMMENT, {
    onSuccess: () => {
      // Force immediate refetch for reply to show immediately
      queryClient.refetchQueries('getJobs');
      queryClient.refetchQueries(['jobComments', jobId]);
      setReplyingTo(null);
      setReplyContent('');
      setMentionedUsers([]);
      toast.success('Reply added successfully');
    },
    onError: () => {
      toast.error('Failed to add reply');
    }
  });

  const deleteCommentMutation = useMutation(DELETE_COMMENT, {
    onSuccess: (_, commentId) => {
      setLocalComments(prev => removeCommentFromLocal(prev, commentId));

      queryClient.refetchQueries(['stickyComments', jobId]);
      queryClient.refetchQueries(['jobComments', jobId]);
      queryClient.refetchQueries('getJobs');

      setOpenDropdown(null);
      toast.success('Comment deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete comment');
      setOpenDropdown(null);
    }
  });

  const handlePinToggle = (commentId: string, isCurrentlyPinned: boolean) => {
    if (isCurrentlyPinned) {
      unpinCommentMutation.mutate(commentId);
    } else {
      pinCommentMutation.mutate(commentId);
    }
  };

  const handleReply = (commentId: string) => {
    setReplyingTo(commentId);
  };

  const handleReplySubmit = async (commentId: string) => {
    if (!replyContent.trim()) return;

    const payload: any = {
      content: replyContent,
      parentCommentId: commentId,
      creator: user.id || user._id
    };

    if (mentionedUsers.length > 0) {
      payload.mentionedUsers = mentionedUsers.map(user => user._id);
    }

    await replyCommentMutation.mutateAsync(payload);
  };

  const handleMentionChange = (
    value: string,
    mentionedUsers: MentionUser[]
  ) => {
    setReplyContent(value);
    setMentionedUsers(mentionedUsers);
  };

  const handleDeleteComment = (commentId: string) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      deleteCommentMutation.mutate(commentId);
    }
  };

  const toggleDropdown = (commentId: string) => {
    setOpenDropdown(openDropdown === commentId ? null : commentId);
  };

  const canDeleteComment = (comment: JobComment): boolean => {
    return getEntityId(comment?.creator) === getEntityId(user);
  };

  const filteredComments = localComments.filter(comment => {
    if (comment.parentCommentId) return false;
    if (filter === CommentType.All) return true;
    return comment.commentType === filter;
  });

  const renderComment = (
    comment: JobComment,
    index: number,
    nestingLevel = 0
  ) => {
    const replies = comment.replies || [];
    const isReply = nestingLevel > 0;

    return (
      <div key={comment.id || comment._id} className="fb-comment-wrapper">
        {/* Main Comment */}
        <div className={classNames('fb-comment', { 'fb-reply': isReply })}>
          <div className="fb-comment-avatar">
            <UserAvatar
              user={comment.creator}
              size={isReply ? 32 : 40}
              isReply={isReply}
            />
          </div>

          <div className="fb-comment-content">
            <div className="fb-comment-bubble">
              <div className="fb-comment-header">
                <Link
                  to={comment?.creator?.username || ''}
                  className="fb-comment-author"
                >
                  {(comment?.creator?.id || comment?.creator?._id) ===
                  (user?.id || user?._id)
                    ? 'You'
                    : `${comment?.creator?.firstName} ${comment?.creator?.lastName}`}
                </Link>
                <div className="fb-comment-badges">
                  <span
                    className={`fb-comment-type-badge ${
                      comment.commentType === CommentType.Field
                        ? 'field-badge'
                        : 'shop-badge'
                    }`}
                  >
                    {comment.commentType}
                  </span>
                  {comment.isSticky && (
                    <span className="fb-comment-pinned-badge">
                      <FontAwesomeIcon icon={faThumbtack} className="me-1" />
                      Pinned
                    </span>
                  )}
                </div>
              </div>

              {comment.content && (
                <div
                  className="fb-comment-text"
                  dangerouslySetInnerHTML={{
                    __html: processCommentContent(comment.content)
                  }}
                />
              )}
            </div>

            <div className="fb-comment-actions">
              <span className="fb-comment-time">
                <HumanReadableTime
                  date={new Date(comment.createdAt as unknown as Date)}
                />
              </span>

              <button
                className="fb-action-btn fb-reply-btn"
                type="button"
                onClick={() => handleReply(comment.id || comment._id)}
              >
                <FontAwesomeIcon icon={faReply} className="me-1" />
                Reply
              </button>

              {!isReply && (
                <button
                  className="fb-action-btn fb-pin-btn"
                  type="button"
                  onClick={() =>
                    handlePinToggle(
                      comment.id || comment._id,
                      comment.isSticky || false
                    )
                  }
                >
                  <FontAwesomeIcon
                    icon={faThumbtack}
                    className={classNames('me-1', {
                      'text-warning': comment.isSticky
                    })}
                  />
                  {comment.isSticky ? 'Unpin' : 'Pin'}
                </button>
              )}

              <Dropdown
                show={openDropdown === (comment.id || comment._id)}
                onToggle={() => toggleDropdown(comment.id || comment._id)}
              >
                <Dropdown.Toggle
                  as="button"
                  className="fb-action-btn fb-more-btn"
                  type="button"
                >
                  <FontAwesomeIcon icon={faEllipsisH} />
                </Dropdown.Toggle>

                <Dropdown.Menu align="end" className="fb-comment-dropdown">
                  {canDeleteComment(comment) && (
                    <Dropdown.Item
                      onClick={() =>
                        handleDeleteComment(comment.id || comment._id)
                      }
                      className="text-danger"
                      disabled={deleteCommentMutation.isLoading}
                    >
                      {deleteCommentMutation.isLoading &&
                      deleteCommentMutation.variables ===
                        (comment.id || comment._id) ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            style={{ width: '12px', height: '12px' }}
                            className="me-2"
                          />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faTrash} className="me-2" />
                          Delete Comment
                        </>
                      )}
                    </Dropdown.Item>
                  )}
                  {!canDeleteComment(comment) && (
                    <Dropdown.Item disabled className="text-muted">
                      No actions available
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            {/* Reply Input */}
            {replyingTo === (comment.id || comment._id) && (
              <div className="fb-reply-input-wrapper">
                <div className="fb-reply-input">
                  <div className="fb-reply-avatar">
                    <UserAvatar user={user} size={32} isReply={true} />
                  </div>
                  <div className="fb-reply-input-content">
                    <MentionInput
                      value={replyContent}
                      onChange={handleMentionChange}
                      placeholder="Write a reply..."
                      rows={2}
                    />
                    <div className="fb-reply-actions">
                      <Button
                        variant="link"
                        size="sm"
                        className="text-muted p-0 me-2"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyContent('');
                          setMentionedUsers([]);
                        }}
                        disabled={replyCommentMutation.isLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() =>
                          handleReplySubmit(comment.id || comment._id)
                        }
                        disabled={
                          !replyContent.trim() || replyCommentMutation.isLoading
                        }
                        className="fb-reply-submit"
                      >
                        {replyCommentMutation.isLoading ? (
                          <Spinner
                            animation="border"
                            size="sm"
                            style={{ width: '12px', height: '12px' }}
                          />
                        ) : (
                          'Reply'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Render Replies */}
        {replies.length > 0 && (
          <div
            className={classNames('fb-replies-container', {
              'has-single-reply': replies.length === 1,
              'has-multiple-replies': replies.length > 1
            })}
          >
            {replies.map((reply, replyIndex) =>
              renderComment(reply, replyIndex, nestingLevel + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={className}>
      <div className="d-flex justify-content-end gap-2 mb-3">
        <Button
          variant={filter === CommentType.All ? 'primary' : 'outline-primary'}
          onClick={() => setFilter(CommentType.All)}
          size="sm"
        >
          All
        </Button>
        <Button
          variant={filter === CommentType.Shop ? 'primary' : 'outline-primary'}
          onClick={() => setFilter(CommentType.Shop)}
          size="sm"
        >
          Shop
        </Button>
        <Button
          variant={
            filter === CommentType.JobStage ? 'primary' : 'outline-primary'
          }
          onClick={() => setFilter(CommentType.JobStage)}
          size="sm"
        >
          Job Stage
        </Button>
        <Button
          variant={filter === CommentType.Field ? 'primary' : 'outline-primary'}
          onClick={() => setFilter(CommentType.Field)}
          size="sm"
        >
          Field
        </Button>
      </div>

      <div
        className="fb-comments-container"
        style={{
          height: comments.length > 0 ? '400px' : '150px',
          overflowY: 'auto'
        }}
      >
        {filteredComments.length === 0 ? (
          <div className="fb-no-comments">
            <FontAwesomeIcon
              icon={faComments}
              size="3x"
              className="text-muted mb-3"
            />
            <p className="text-muted mb-0">No comments found</p>
          </div>
        ) : (
          <div className="fb-comments-list">
            {filteredComments.map((comment, index) =>
              renderComment(comment, index, 0)
            )}
          </div>
        )}
      </div>
    </div>
  );
};
